import { IFile, TIMEZONE_KEY } from './common';
import { LoginType } from './account';
import { IMember } from './member';

/**
 * 企业类型(内置/集成)
 */
export enum CorpType {
    // 内置企业
    Default = 'Default',
    // 钉钉
    DingTalk = 'DingTalk',
    // 企业微信
    WxWork = 'WxWork',
    // 飞书
    Lark = 'Lark'
}

export enum CorpThemeColor {
    teal = 'teal', // 默认企业风格
    red = 'red',
    orange = 'orange',
    yellow = 'yellow',
    green = 'green',
    cyan = 'cyan',
    blue = 'blue',
    indigo = 'indigo',
    violet = 'violet',
    purple = 'purple',
    pink = 'pink',
    bluegrey = 'bluegrey'
}

export interface Timezone {
    /** eg: 'Etc/GMT-8' */
    etc: TIMEZONE_KEY | 'None';
    /** 唯一，eg: 'Asia/Shanghai'  */
    label: string;
}

/**
 * 水印配置
 */
export interface WatermarkConfig {
    /** 是否开启 */
    enabled: boolean;
    color?: WATERMARK_COLOR;
    density?: WATERMARK_DENSITY;
}

/** 水印颜色 */
export enum WATERMARK_COLOR {
    /** 标准 */
    NORMAL = 'normal',
    /** 浅色 */
    LIGHT = 'light',
    /** 深色 */
    DEEP = 'deep'
}

/** 水印深浅 */
export enum WATERMARK_DENSITY {
    /** 标准 */
    NORMAL = 'normal',
    /** 密集 */
    DENSE = 'dense',
    /** 稀疏 */
    SPARSE = 'sparse'
}

export interface Region {
    use12Hour: boolean;
    dateTimeFormat: {
        ym: string;
        ymd: string;
        hm: string;
        hms: string;
        ymd_hm: string;
        ymd_hms: string;
    };
    weekStart: number;
    yearStart: number;
    decimalSeparator: string;
    thousandSeparator: string;
}

export interface UserTenant {
    tenant_id: string;
    name: string;
    // todo skye 待替换为 is_corp_owner
    isCorpOwner: boolean;
    // 标识非平台租户
    isNotMigrated?: boolean;
}

export interface UserTenantsFilterParams {
    tenant_ids: string[];
    jsy_user_id?: string;
}

export interface UserTenantsFilterResult {
    tenant_ids: string[];
    /** 非平台租户 */
    non_platform_tenants?: {
        tenant_id: string;
        name: string;
        is_corp_owner: boolean;
    }[];
}

export type UserTenantsFilter = (
    params: UserTenantsFilterParams
) => UserTenantsFilterResult | Promise<UserTenantsFilterResult>;

/**
 * SP 配置接口
 */
interface ISsoSPConfig {
    // 是否启用
    enable: boolean;
    // 单点登录 Endpoint
    sso_endpoint: string;
    // 单点登出 Endpoint
    slo_endpoint?: string;
}

/**
 * SAML 2 SP 配置接口
 */
interface ISamlSPConfig extends ISsoSPConfig {
    // 签名算法
    sig_alg: string;
    // 签名证书
    sig_crt: string;
    // 认证请求发起人 (IdP Identity)
    issuer: string;
}

/**
 * JSON-Web-Token SP 配置接口
 */
interface ICustomSPConfig extends ISsoSPConfig {
    // 签名算法
    sig_alg: string;
    // 签名密钥
    secret: string;
    // 认证请求发起人 (IdP Identity)
    issuer?: string;
}

/**
 * 单点登录类型
 *
 * @enum SsoType
 */
export enum SsoType {
    cas = 'cas',
    saml = 'saml',
    custom = 'custom'
}

export interface ISsoConfig {
    type?: SsoType;
    config?: ISamlSPConfig | ICustomSPConfig;
    urls?: {
        acs: string;
        metadata: string;
        slo: string;
    };
}

export interface ILoginChannel {
    channel:
        | LoginType.Account
        | LoginType.SSO
        | LoginType.Wxwork
        | LoginType.Dingtalk
        | LoginType.Lark;
    enable: boolean;
}

/**
 * CheckBox 可视状态
 */
export enum CheckBoxVisualFlag {
    checked = 'checked',
    unchecked = 'unchecked',
    hidden = 'hidden'
}

/**
 * 企业自定义登录页配置接口
 */
export interface ICustomLoginConfig {
    enable: boolean;
    logo: IFile;
    banner: IFile;
    login_channels: ILoginChannel[];
    has_remember: CheckBoxVisualFlag;
    has_register: boolean;
    has_locale: boolean;
}

export interface RegionConfig {
    country_locale: string;
    date_format: string;
    time_format: string;
    number_separator: string;
    decimal_separator: string;
    week_start: string;
    year_start: string;
}

/**
 * Cp模式授权信息
 */
interface ICpAuth {
    agent_id: string;
    is_canceled?: boolean;
    suite_id?: string;
    corp_secret: string;
    secret_token?: string;
    secret_encoding_aes_key?: string;
    sso_secret?: string;
    app_key?: string;
    is_customized_app?: boolean;
}

// 企业风格
export interface ICorpThemeConfig {
    enabled: boolean;
    color?: CorpThemeColor;
}

/** 企业高管（提醒屏蔽）配置 */
export interface CorpExecutive {
    member?: Pick<
        IMember,
        '_id' | 'type' | 'status' | 'nickname' | 'username' | 'avatar' | 'label'
    >;
    whitelist: string[];
}

export interface TenantConf {
    /**
     * 租户ID，用于在平台标识一个企业，取代之前的 corp_id
     * 业务侧功能大部分时候都应该使用该租户ID，而非 corp_id
     */
    tenant_id: string;
    corp_name: string;
    // 企业类型
    corp_type: CorpType;
    /** 当前规范的企业登录页完整 URL. */
    current_corp_url: string;
    /** 历史企业登录页完整 URL. */
    legacy_corp_url?: string;
    has_sso: boolean;
    sso_config?: ISsoConfig;
    has_custom_login: boolean;
    custom_login_config?: ICustomLoginConfig;
    wx_authorizer?: {
        nickname: string;
        avatar: string;
        raw_id: string;
    };
    wx_auth_url?: string;
    wx_bind_url?: string;
    has_isv_auth: boolean;
    has_cp_auth: boolean;
    has_watermark: boolean;
    // 租户语言
    locale?: string;
    timezone?: Timezone;
    region?: RegionConfig;
    // 是否启用微信授权
    wx_enable?: boolean;
    // 是否开启全局水印
    watermark?: WatermarkConfig;
    // // Corp Secret 模式企业授权信息
    cp_auth?: ICpAuth;
    // 套餐是否包含自定义主题色
    has_theme: boolean;
    // 企业自定义主题色配置
    corp_theme?: ICorpThemeConfig;
    // 企业是否开启登录二次验证
    mfa_enabled?: boolean;
    corp_executive?: CorpExecutive[];
    // 是否为企微密文模式（注意和企微isv密文不一样）
    is_wxwork_id_open?: boolean;
    /**
     * 对于集成模式，该参数是第三方企业直接提供的id，用于标识一个企业
     */
    integrate_id: string;
    /** 飞书应用信息 */
    lark_permission_scopes?: {
        app_id: string;
        granted_scopes: string[];
        ungranted_scopes: string[];
    };
}
